// A navigable chart: the wheel zooms around the pointer, a drag pans, a
// double click shows as much as the limits allow. Wraps the chart it moves;
// the span lives with the caller, which redraws the chart for it.

import { useEffect, useRef, type ReactNode } from "react";
import { CHART_MARGIN, yAxisProps } from "@/platform/ui/charts/theme.js";
import { panSpan, widestSpan, zoomSpan, type Span } from "@/platform/ui/charts/viewport.js";

/** Where the plot sits inside the chart, horizontally: the y axis on the
 *  left, the margin on the right. */
const PLOT_LEFT = yAxisProps.width + CHART_MARGIN.left;
const PLOT_RIGHT = CHART_MARGIN.right;
const STEP = 1.18;

export function ChartViewport({
  span,
  domain,
  minSpan,
  maxSpan = Infinity,
  onChange,
  children,
}: {
  span: Span;
  domain: Span;
  minSpan: number;
  /** The widest span; none when absent. */
  maxSpan?: number;
  onChange: (span: Span) => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const latest = useRef({ span, domain, minSpan, maxSpan, onChange });
  latest.current = { span, domain, minSpan, maxSpan, onChange };

  // The wheel is listened to directly: React's listener is passive, and a
  // zoom must keep the page from scrolling under it.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      const { span: s, domain: d, minSpan: lo, maxSpan: hi, onChange: change } = latest.current;
      const box = el.getBoundingClientRect();
      const width = box.width - PLOT_LEFT - PLOT_RIGHT;
      if (width <= 0) return;
      e.preventDefault();
      const anchor = (e.clientX - box.left - PLOT_LEFT) / width;
      change(zoomSpan(s, d, e.deltaY > 0 ? STEP : 1 / STEP, anchor, lo, hi));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const drag = useRef<{ x: number; span: Span; moved: boolean } | null>(null);

  return (
    <div
      ref={ref}
      className="cursor-grab touch-pan-y select-none active:cursor-grabbing"
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        drag.current = { x: e.clientX, span, moved: false };
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (!d) return;
        const dx = e.clientX - d.x;
        if (!d.moved && Math.abs(dx) < 4) return;
        d.moved = true;
        const width = e.currentTarget.getBoundingClientRect().width - PLOT_LEFT - PLOT_RIGHT;
        if (width <= 0) return;
        const perPixel = (d.span[1] - d.span[0]) / width;
        onChange(panSpan(d.span, domain, -dx * perPixel));
      }}
      onPointerUp={() => (drag.current = null)}
      onPointerCancel={() => (drag.current = null)}
      onDoubleClick={() => onChange(widestSpan(span, domain, maxSpan))}
    >
      {children}
    </div>
  );
}
